import React, {useState, useEffect} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {FiSearch, FiShoppingCart, FiHeart, FiUser, FiLogIn} from 'react-icons/fi';
import {
    FaTimes,
    FaUserCircle,
    FaShoppingBag,
    FaSignOutAlt,
    FaInfoCircle,
    FaHome, FaPhone, FaPhoneAlt
} from 'react-icons/fa';
import {isLogin} from "../../auth";
import SearchOverlay from "../SearchOverlay/SearchOverlay";

const Header = () => {
    const isAuthenticated = isLogin()
    const {items: cartItems} = useSelector((state) => state.cart);
    const {items: wishlistItems} = useSelector((state) => state.wishlist);
    const [searchOpen, setSearchOpen] = useState(false);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Add scroll event listener
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileMenuOpen && !event.target.closest('.profile-menu-container')) {
                setProfileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [profileMenuOpen]);

    const handleLogout = () => {
        dispatch({type: 'LOGOUT'});
        localStorage.removeItem('auth0');

        // Verify auth0 is removed before navigation
        if (!localStorage.getItem('auth0')) {
            navigate('/account-login');
            setProfileMenuOpen(false);
        } else {
            // Try removal again if needed
            localStorage.removeItem('auth0');
            navigate('/account-login');
            setProfileMenuOpen(false);
        }
    };

    const toggleSearch = () => {
        setSearchOpen(!searchOpen);
    };

    const toggleProfileMenu = () => {
        setProfileMenuOpen(!profileMenuOpen);
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    return (
        <>
            <header className={`header-area ${scrolled ? 'sticky-active' : ''}`}>
                <div className="main-header" style={{
                    boxShadow: scrolled ? '0 2px 10px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.3s ease'
                }}>
                    <div className="container">
                        <div className="row align-items-center py-3">
                            {/* Logo */}
                            <div className="col-6 col-sm-3 col-lg-2">
                                <div className="header-logo">
                                    <Link to="/">
                                        <img
                                            className="logo-main"
                                            src="/assets/images/logo.png"
                                            style={{
                                                width: "80px",
                                                height: "80px",
                                                maxWidth: "100%",
                                                objectFit: "contain"
                                            }}
                                            alt="Logo"
                                        />
                                    </Link>
                                </div>
                            </div>

                            {/* Main Navigation */}
                            <div className="col-lg-7 d-none d-lg-block">
                                <div className="header-navigation">
                                    <ul className="main-nav justify-content-start" style={{margin: 0}}>
                                        <li className="nav-item has-submenu">
                                            <Link
                                                to="/"
                                                className="nav-link"
                                                onMouseEnter={(e) => e.target.style.color = '#D4B675'}
                                                onMouseLeave={(e) => e.target.style.color = ''}
                                            >Home</Link>
                                        </li>
                                        <li className="nav-item">
                                            <Link
                                                to="/about-us"
                                                className="nav-link"
                                                onMouseEnter={(e) => e.target.style.color = '#D4B675'}
                                                onMouseLeave={(e) => e.target.style.color = ''}
                                            >About</Link>
                                        </li>
                                        <li className="nav-item">
                                            <Link
                                                to="/contact"
                                                className="nav-link"
                                                onMouseEnter={(e) => e.target.style.color = '#D4B675'}
                                                onMouseLeave={(e) => e.target.style.color = ''}
                                            >Contact</Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            {/* Header Actions */}
                            <div className="col-6 col-sm-9 col-lg-3">
                                <div className="header-action justify-content-end">
                                    {/* Search Button */}
                                    {isAuthenticated ? (
                                        <>
                                            <button
                                                className="header-action-btn"
                                                type="button"
                                                onClick={toggleSearch}
                                                aria-label="Search"
                                            >
                                              <span className="icon" style={{color: '#333'}}>
                                                <FiSearch size={22}/>
                                              </span>
                                            </button>
                                            {/* Cart Button with Counter */}
                                            <Link className="header-action-btn position-relative" to="/product-cart"
                                                  aria-label="Cart">
                                              <span className="icon" style={{color: '#333'}}>
                                                <FiShoppingCart size={22}/>
                                              </span>
                                                {cartItems && cartItems.length > 0 && (
                                                    <span className="badge position-absolute"
                                                          style={{
                                                              top: '-8px',
                                                              right: '-8px',
                                                              fontSize: '10px',
                                                              backgroundColor: '#D4B675',
                                                              color: '#fff',
                                                              borderRadius: '50%',
                                                              minWidth: '18px',
                                                              height: '18px',
                                                              display: 'flex',
                                                              alignItems: 'center',
                                                              justifyContent: 'center'
                                                          }}>
                                                    {cartItems.length}
                                                  </span>
                                                )}
                                            </Link>

                                            {/* Wishlist Button with Counter */}
                                            <Link className="header-action-btn position-relative" to="/product-wishlist"
                                                  aria-label="Wishlist">
                                              <span className="icon" style={{color: '#333'}}>
                                                <FiHeart size={22}/>
                                              </span>
                                                {wishlistItems && wishlistItems.length > 0 && (
                                                    <span className="badge position-absolute"
                                                          style={{
                                                              top: '-8px',
                                                              right: '-8px',
                                                              fontSize: '10px',
                                                              backgroundColor: '#D4B675',
                                                              color: '#fff',
                                                              borderRadius: '50%',
                                                              minWidth: '18px',
                                                              height: '18px',
                                                              display: 'flex',
                                                              alignItems: 'center',
                                                              justifyContent: 'center'
                                                          }}>
                                                    {wishlistItems.length}
                                                  </span>
                                                )}
                                            </Link>

                                            {/* Profile Menu */}
                                            <div className="position-relative d-inline-block profile-menu-container">
                                                <button
                                                    className="header-action-btn"
                                                    type="button"
                                                    onClick={toggleProfileMenu}
                                                    aria-label="Profile"
                                                >
                                                  <span className="icon" style={{color: '#333'}}>
                                                    <FiUser size={22}/>
                                                  </span>
                                                </button>

                                                {profileMenuOpen && (
                                                    <div
                                                        className="profile-dropdown position-absolute bg-white shadow-lg rounded-3 py-2"
                                                        style={{
                                                            top: '45px',
                                                            right: '0',
                                                            width: '220px',
                                                            zIndex: 1000,
                                                            border: '1px solid #eee',
                                                            animation: 'fadeIn 0.2s ease-in-out'
                                                        }}>
                                                        <Link to="/my-account"
                                                              className="dropdown-item py-2 px-3 d-flex align-items-center"
                                                              onClick={() => setProfileMenuOpen(false)}>
                                                            <FaUserCircle className="me-2"
                                                                          style={{color: '#D4B675'}}/> My Account
                                                        </Link>
                                                        <Link to="/my-orders"
                                                              className="dropdown-item py-2 px-3 d-flex align-items-center"
                                                              onClick={() => setProfileMenuOpen(false)}>
                                                            <FaShoppingBag className="me-2"
                                                                           style={{color: '#D4B675'}}/> My Orders
                                                        </Link>
                                                        <Link to="/product-wishlist"
                                                              className="dropdown-item py-2 px-3 d-flex align-items-center"
                                                              onClick={() => setProfileMenuOpen(false)}>
                                                            <FiHeart className="me-2" style={{color: '#D4B675'}}/> My
                                                            Wishlist
                                                        </Link>
                                                        <div className="dropdown-divider"></div>
                                                        <button onClick={handleLogout}
                                                                className="dropdown-item py-2 px-3 d-flex align-items-center text-danger">
                                                            <FaSignOutAlt className="me-2"/> Logout
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <Link
                                            className="login-btn"
                                            to="/account-login"
                                            style={{
                                                backgroundColor: '#D4B675',
                                                color: '#fff',
                                                padding: '8px 20px',
                                                borderRadius: '50px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontWeight: '500',
                                                boxShadow: '0 4px 6px rgba(212, 182, 117, 0.2)',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.backgroundColor = '#c0a35f';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = '0 6px 12px rgba(212, 182, 117, 0.3)';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.backgroundColor = '#D4B675';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 4px 6px rgba(212, 182, 117, 0.2)';
                                            }}
                                        >
                                            <FiLogIn size={20}/>
                                            <span>Sign In</span>
                                        </Link>
                                    )}

                                    {/* Mobile Menu Button */}
                                    <button
                                        className="header-menu-btn d-lg-none"
                                        type="button"
                                        onClick={toggleMobileMenu}
                                        aria-label="Toggle mobile menu"
                                        style={{marginLeft: '10px'}}
                                    >
                                        <span style={{backgroundColor: '#333'}}></span>
                                        <span style={{backgroundColor: '#333'}}></span>
                                        <span style={{backgroundColor: '#333'}}></span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Search Overlay */}
            <SearchOverlay isOpen={searchOpen} onClose={toggleSearch} />

            {/* Mobile Menu - UPDATED */}
            {mobileMenuOpen && (
                <div className="mobile-menu-overlay position-fixed w-100 h-100"
                     style={{
                         top: 0,
                         left: 0,
                         backgroundColor: 'rgba(0,0,0,0.5)',
                         zIndex: 1040
                     }}
                     onClick={toggleMobileMenu}>
                    <div
                        className="mobile-menu-container position-fixed bg-white h-100 p-0"
                        style={{
                            top: 0,
                            left: 0,
                            width: '85%',
                            maxWidth: '320px',
                            zIndex: 1050,
                            overflowY: 'auto',
                            transform: 'translateX(0)',
                            transition: 'transform 0.3s ease',
                            boxShadow: '5px 0 15px rgba(0,0,0,0.1)'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Mobile Menu Header */}
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                            <div>
                                <Link to="/">
                                    <img
                                        src="/assets/images/logo.png"
                                        style={{
                                            width: '120px',
                                            height: '90px',
                                            objectFit: 'contain'
                                        }}
                                        alt="Logo"
                                    />
                                </Link>
                            </div>
                            <button
                                className="bg-transparent border-0 text-dark"
                                onClick={toggleMobileMenu}
                                style={{fontSize: '24px'}}
                                aria-label="Close menu"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Navigation Menu - Main options matched with desktop */}
                        <ul className="nav flex-column p-0">
                            <li className="nav-item">
                                <Link to="/" className="nav-link px-4 py-3 border-bottom" onClick={toggleMobileMenu}>
                                    <FaHome className="me-2" style={{color: '#D4B675'}}/> Home
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link to="/about-us" className="nav-link px-4 py-3 border-bottom"
                                      onClick={toggleMobileMenu}>
                                    <FaInfoCircle className="me-2" style={{color: '#D4B675'}}/> About
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link to="/contact" className="nav-link px-4 py-3 border-bottom"
                                      onClick={toggleMobileMenu}>
                                    <FaPhoneAlt className="me-2" style={{color: '#D4B675'}}/> Contact
                                </Link>
                            </li>
                        </ul>

                        {/* Authenticated User Menu Items */}
                        {isAuthenticated ? (
                            <div>
                                <ul className="nav flex-column p-0">
                                    {/* Cart Item */}
                                    <li className="nav-item">
                                        <Link to="/product-cart"
                                              className="nav-link px-4 py-3 border-bottom d-flex justify-content-between align-items-center"
                                              onClick={toggleMobileMenu}>
                                            <div>
                                                <FiShoppingCart className="me-2" style={{color: '#D4B675'}}/> Cart
                                            </div>
                                            {cartItems && cartItems.length > 0 && (
                                                <span className="badge"
                                                      style={{
                                                          backgroundColor: '#D4B675',
                                                          color: '#fff',
                                                          borderRadius: '50%',
                                                          minWidth: '22px',
                                                          height: '22px',
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center'
                                                      }}>
                                                    {cartItems.length}
                                                </span>
                                            )}
                                        </Link>
                                    </li>

                                    {/* Wishlist Item */}
                                    <li className="nav-item">
                                        <Link to="/product-wishlist"
                                              className="nav-link px-4 py-3 border-bottom d-flex justify-content-between align-items-center"
                                              onClick={toggleMobileMenu}>
                                            <div>
                                                <FiHeart className="me-2" style={{color: '#D4B675'}}/> Wishlist
                                            </div>
                                            {wishlistItems && wishlistItems.length > 0 && (
                                                <span className="badge"
                                                      style={{
                                                          backgroundColor: '#D4B675',
                                                          color: '#fff',
                                                          borderRadius: '50%',
                                                          minWidth: '22px',
                                                          height: '22px',
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center'
                                                      }}>
                                                    {wishlistItems.length}
                                                </span>
                                            )}
                                        </Link>
                                    </li>

                                    {/* My Account */}
                                    <li className="nav-item">
                                        <Link to="/my-account" className="nav-link px-4 py-3 border-bottom"
                                              onClick={toggleMobileMenu}>
                                            <FaUserCircle className="me-2" style={{color: '#D4B675'}}/> My Account
                                        </Link>
                                    </li>

                                    {/* My Orders */}
                                    <li className="nav-item">
                                        <Link to="/my-orders" className="nav-link px-4 py-3 border-bottom"
                                              onClick={toggleMobileMenu}>
                                            <FaShoppingBag className="me-2" style={{color: '#D4B675'}}/> My Orders
                                        </Link>
                                    </li>

                                    {/* Logout */}
                                    <li className="nav-item">
                                        <button
                                            className="nav-link text-danger px-4 py-3 border-bottom d-flex align-items-center w-100 bg-transparent"
                                            onClick={() => {
                                                handleLogout();
                                                toggleMobileMenu();
                                            }}
                                        >
                                            <FaSignOutAlt className="me-2"/> Logout
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        ) : (
                            <div className="p-4">
                                <Link
                                    to="/account-login"
                                    className="btn w-100 d-flex align-items-center justify-content-center gap-2"
                                    onClick={toggleMobileMenu}
                                    style={{
                                        backgroundColor: '#D4B675',
                                        color: '#fff',
                                        padding: '12px 15px',
                                        borderRadius: '50px',
                                        fontWeight: '500',
                                        boxShadow: '0 4px 8px rgba(212, 182, 117, 0.25)',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <FiLogIn size={20}/>
                                    <span>Sign In</span>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;